import { motion } from 'framer-motion';
import { TRACKS } from '@/constants';
import { Calendar, Users, Presentation } from 'lucide-react';

export default function Program() {
  return (
    <div className="bg-white" data-testid="program-page">
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl font-unbounded font-black mb-8"
            data-testid="program-title"
          >
            The Program
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-manrope max-w-4xl"
          >
            A 4-week residential experience designed to transform AI curiosity into shipped products.
          </motion.p>
        </div>
      </section>

      <section className="py-24" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Program Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="border-l-4 border-accent bg-gray-50 p-8">
              <Users className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">8 Residents per House</h3>
              <p className="font-manrope text-gray-700">Cohort-based model. Live with builders from diverse backgrounds, all focused on AI.</p>
            </div>
            <div className="border-l-4 border-accent bg-gray-50 p-8">
              <Calendar className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">4 Weeks Intensive</h3>
              <p className="font-manrope text-gray-700">Structured daily schedule with workshops, build time, and mentorship.</p>
            </div>
            <div className="border-l-4 border-accent bg-gray-50 p-8">
              <Presentation className="text-accent mb-4" size={40} />
              <h3 className="text-2xl font-unbounded font-bold mb-4">Demo Day</h3>
              <p className="font-manrope text-gray-700">Showcase your projects to partners, investors, and the community.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50" data-testid="tracks-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Tracks</h2>
          <div className="space-y-8">
            {TRACKS.map((track, index) => (
              <motion.div
                key={track.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-black/10 p-8"
                data-testid={`track-${track.id}`}
              >
                <h3 className="text-3xl font-unbounded font-bold mb-4">{track.title}</h3>
                <p className="text-lg font-manrope text-gray-700 mb-6">{track.description}</p>
                <div className="flex flex-wrap gap-4">
                  {track.outcomes.map((outcome) => (
                    <span key={outcome} className="bg-accent text-white px-4 py-2 text-sm font-manrope font-medium">
                      {outcome}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="weekly-cadence-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">Weekly Cadence</h2>
          <div className="space-y-6">
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Monday-Tuesday: Workshops</h3>
              <p className="font-manrope text-gray-700">Learn new tools, techniques, and frameworks from experts.</p>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Wednesday-Friday: Build Sprints</h3>
              <p className="font-manrope text-gray-700">Hands-on building time with mentor support and peer collaboration.</p>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Saturday: Demo + Review</h3>
              <p className="font-manrope text-gray-700">Show your progress, get feedback, and iterate.</p>
            </div>
            <div className="bg-white border-l-4 border-accent p-8">
              <h3 className="text-2xl font-unbounded font-bold mb-4">Sunday: Community Day</h3>
              <p className="font-manrope text-gray-700">Network, explore the city, recharge.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-black text-white" data-testid="what-youll-build-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-unbounded font-black mb-12">What You'll Build</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-unbounded font-bold mb-4">AI Chatbots</h3>
              <p className="font-manrope text-gray-300">Customer support, personal assistants, domain experts.</p>
            </div>
            <div>
              <h3 className="text-xl font-unbounded font-bold mb-4">AI Agents</h3>
              <p className="font-manrope text-gray-300">Autonomous workflows, research tools, task automation.</p>
            </div>
            <div>
              <h3 className="text-xl font-unbounded font-bold mb-4">Content Systems</h3>
              <p className="font-manrope text-gray-300">Newsletters, video workflows, social media automation.</p>
            </div>
            <div>
              <h3 className="text-xl font-unbounded font-bold mb-4">Business Tools</h3>
              <p className="font-manrope text-gray-300">Internal automations, analytics dashboards, ops tools.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
